import { z } from 'zod';
import { AiModelTier } from './ai.js';
import { ChecklistItemsInput } from './task-checklist.js';
export const AssistantAction = z.enum(['project', 'task', 'sprint', 'issue', 'report', 'todo']);
export const AssistantValue = z.union([
    z.string().max(20000),
    z.number(),
    z.boolean(),
    z.array(z.string().max(500)).max(100),
    ChecklistItemsInput,
    z.null(),
]);
export function assistantValueText(value) {
    return Array.isArray(value)
        ? value
            .map((item) => typeof item === 'string'
            ? item
            : `${item.text}${item.weight === null ? '' : ` (${item.weight}%)`}`)
            .join('\n')
        : String(value ?? '');
}
export const AssistantField = z.object({
    key: z.string(),
    label: z.string(),
    kind: z.enum([
        'text',
        'date',
        'number',
        'boolean',
        'single',
        'multiple',
        'checklist',
        'color',
        'time',
    ]),
    optional: z.boolean().default(false),
    options: z.array(z.object({ value: z.string(), label: z.string() })).default([]),
});
export const AssistantDraft = z.object({
    status: z.literal('ai_suggested'),
    action: AssistantAction,
    values: z.record(z.string(), AssistantValue),
    skipped: z.array(z.string()).default([]),
    labels: z.record(z.string(), z.string()).default({}),
});
export const AssistantLink = z.object({ label: z.string(), href: z.string().startsWith('/') });
/**
 * Câu hỏi trợ lý tự tổng hợp được ngay trong khung chat — APM-CHAT2-c. Tin nhắn mang nó thì
 * có nút "Trợ lý tự tổng hợp" cạnh shortcut; bấm mới chạy (lượt `insight`).
 */
export const AssistantInsight = z.enum(['team_performance']);
export const AssistantMessage = z.object({
    id: z.string(),
    role: z.enum(['user', 'assistant']),
    text: z.string(),
    at: z.string(),
    links: z.array(AssistantLink).default([]),
    sources: z.array(z.string()).default([]),
    runId: z.string().optional(),
    knowledgeVersion: z.string().optional(),
    insight: AssistantInsight.optional(),
});
export const AssistantState = z.object({
    lastCreatedReportId: z.uuid().optional(),
    messages: z.array(AssistantMessage).max(1000).default([]),
    draft: AssistantDraft.nullable().default(null),
    suggestions: z.array(z.string()).max(3).default([]),
    suggestionsFromAi: z.boolean().default(false),
    lastRequestId: z.string().nullable().default(null),
    completedRequestId: z.string().nullable().default(null),
});
export const AssistantSession = z.object({
    id: z.uuid(),
    title: z.string(),
    version: z.number(),
    updatedAt: z.string(),
    busy: z.boolean(),
    state: AssistantState,
    field: AssistantField.nullable(),
    ready: z.boolean(),
});
/** Ba gợi ý đầu phiên — xếp hạng tất định từ lịch sử của chính người gọi, không gọi model (APM-CHAT2-b). */
export const AssistantSuggestions = z.object({ suggestions: z.array(z.string()).max(3) });
/**
 * Ba mức model cho khung trò chuyện — APM-CHAT2-d. `model` là model **đầu chuỗi** sẽ được gọi
 * cho mức ấy với key đang phục vụ người dùng (cùng luật với Cài đặt → Key AI); `null` khi chưa
 * giải được. `enabled: false` = AI chưa sẵn sàng, web ẩn ô chọn.
 */
export const AssistantModels = z.object({
    enabled: z.boolean(),
    defaultTier: AiModelTier,
    tiers: z.array(z.object({ tier: AiModelTier, model: z.string().nullable() })),
});
export const AssistantCreateInput = z.object({ runId: z.uuid().optional() }).default({});
export const AssistantTurnInput = z.object({
    requestId: z.uuid(),
    version: z.number().int().nonnegative(),
    text: z.string().trim().max(4000).default(''),
    operation: z
        .enum(['message', 'answer', 'skip', 'back', 'cancel', 'confirm', 'start', 'insight'])
        .default('message'),
    value: AssistantValue.optional(),
    action: AssistantAction.optional(),
    insight: AssistantInsight.optional(),
    /** Mức model người dùng chọn cho lượt này (APM-CHAT2-d); bỏ trống = mức của skill. */
    modelTier: AiModelTier.optional(),
});
/** Dữ liệu của chính người hỏi mà chat tra được tại chỗ — APM-CHAT2-e; việc quá hạn và tiến độ dự án APM-CHAT2-i. */
export const AssistantLookup = z.enum([
    'managed_projects',
    'my_projects',
    'my_tasks',
    'overdue_tasks',
    'project_health',
]);
export const AssistantModelOutput = z.object({
    intent: z.enum(['help', 'create', 'answer', 'summarize_report', 'lookup', 'outside', 'unknown']),
    /** Chỉ với `lookup`; `null` = câu tra cứu mà chat chưa có đường đọc. */
    lookup: AssistantLookup.nullable().default(null),
    reportTarget: z
        .union([z.literal('recent'), z.uuid()])
        .nullable()
        .optional(),
    articleIds: z.array(z.string()).max(3),
    action: AssistantAction.nullable(),
    value: AssistantValue,
    suggestionIds: z.array(z.string()).max(3),
    projectHint: z.string().max(200).nullable().default(null),
});
export const AssistantSessionList = z.object({
    items: z.array(AssistantSession.pick({ id: true, title: true, version: true, updatedAt: true })),
    total: z.number(),
    otherSessions: z.array(AssistantSession.pick({ id: true, version: true })),
});
//# sourceMappingURL=assistant.js.map